import { useEffect, useMemo, useRef } from 'react'

import { useSelector } from 'react-redux'
import type { KeyedMutator } from 'swr'

import type { MenuResponse, StandardRecipeItem } from '@library/api-menu-experience'
import type { MenuURN, RecipeURN, RecipeUUID } from '@library/menu-service'

import { getBasketRecipes, getNumPortions } from 'selectors/basket'
import { getBasketRecipeNumPortions, getBasketRecipeQuantity } from 'utils/basket'

import { useMenuExperienceRecipeTileRefresh } from './useMenuExperienceRecipeTileRefresh'

type SelectRecipeGridOption = (
  originalRecipeId: RecipeUUID,
  recipeId: RecipeUUID,
  numPortions: number,
  recipeUrn?: RecipeURN,
) => Promise<void>

type RecipeTileIdentifiers = {
  originalRecipeId?: RecipeUUID
  recipeUrn?: RecipeURN
}

type UseMenuExperienceBasketRecipeTileSyncArgs = {
  menuCacheIdentity: string
  menuContent?: MenuResponse['content']
  menuId: string
  menuUrn?: MenuURN
  mutateMenu: KeyedMutator<MenuResponse>
  selectRecipeGridOption?: SelectRecipeGridOption
}

const getStandardRecipeItems = (
  contentBlock: MenuResponse['content'][number],
): StandardRecipeItem[] => {
  switch (contentBlock.type) {
    case 'recipe-grid':
      return contentBlock.items
    case 'campaign-banner':
      return contentBlock.items.filter((item) => item.type === 'standard-recipe')
    case 'compact-campaign-banner':
      return contentBlock.items
    default:
      return []
  }
}

const getRecipeTileIdentifiers = (
  menuContent: MenuResponse['content'] | undefined,
  recipeId: RecipeUUID,
): RecipeTileIdentifiers => {
  for (const contentBlock of menuContent ?? []) {
    for (const recipe of getStandardRecipeItems(contentBlock)) {
      if (recipe.id === recipeId) {
        return { recipeUrn: recipe.urn }
      }

      const optionIndex = recipe.optionIds?.indexOf(recipeId) ?? -1

      if (optionIndex >= 0) {
        return {
          originalRecipeId: recipe.id,
          recipeUrn: recipe.optionUrns?.[optionIndex],
        }
      }
    }
  }

  return {}
}

export const useMenuExperienceBasketRecipeTileSync = ({
  menuCacheIdentity,
  menuContent,
  menuId,
  menuUrn,
  mutateMenu,
  selectRecipeGridOption,
}: UseMenuExperienceBasketRecipeTileSyncArgs) => {
  const basketRecipes = useSelector(getBasketRecipes)
  const basketNumPortions = useSelector(getNumPortions)
  const { refreshRecipeTile } = useMenuExperienceRecipeTileRefresh({
    menuId,
    menuUrn,
    mutateMenu,
  })
  const previousSnapshotRef = useRef(new Map<RecipeUUID, number>())
  const previousMenuIdRef = useRef(menuId)
  const previousMenuCacheIdentityRef = useRef(menuCacheIdentity)

  const snapshot = useMemo(() => {
    const nextSnapshot = new Map<RecipeUUID, number>()

    basketRecipes.forEach((recipe, recipeId) => {
      const quantity = getBasketRecipeQuantity(recipe)

      if (quantity > 0) {
        nextSnapshot.set(recipeId, getBasketRecipeNumPortions(recipe, basketNumPortions))
      }
    })

    return nextSnapshot
  }, [basketNumPortions, basketRecipes])

  useEffect(() => {
    const hasMenuChanged = previousMenuIdRef.current !== menuId
    const hasMenuCacheChanged = previousMenuCacheIdentityRef.current !== menuCacheIdentity
    const shouldReconcileAll = hasMenuChanged || hasMenuCacheChanged
    const previousSnapshot = previousSnapshotRef.current
    const snapshotToCompare = shouldReconcileAll ? new Map<RecipeUUID, number>() : previousSnapshot

    previousSnapshotRef.current = snapshot
    previousMenuIdRef.current = menuId
    previousMenuCacheIdentityRef.current = menuCacheIdentity
    snapshot.forEach((numPortions, recipeId) => {
      const previousNumPortions = snapshotToCompare.get(recipeId)
      const { originalRecipeId: matchingOriginalRecipeId, recipeUrn } = getRecipeTileIdentifiers(
        menuContent,
        recipeId,
      )
      const originalRecipeId =
        previousNumPortions === undefined ? matchingOriginalRecipeId : undefined
      const hasCustomPortionsBeenAdded =
        previousNumPortions === undefined && numPortions !== basketNumPortions
      const havePortionsChanged =
        previousNumPortions !== undefined && previousNumPortions !== numPortions

      if (originalRecipeId && selectRecipeGridOption) {
        void selectRecipeGridOption(originalRecipeId, recipeId, numPortions, recipeUrn)
      } else if (hasCustomPortionsBeenAdded || havePortionsChanged) {
        void refreshRecipeTile(recipeId, numPortions, recipeUrn)
      }
    })

    previousSnapshot.forEach((_numPortions, recipeId) => {
      if (!snapshot.has(recipeId)) {
        const { recipeUrn } = getRecipeTileIdentifiers(menuContent, recipeId)

        void refreshRecipeTile(recipeId, basketNumPortions, recipeUrn)
      }
    })
  }, [
    basketNumPortions,
    menuCacheIdentity,
    menuContent,
    menuId,
    refreshRecipeTile,
    selectRecipeGridOption,
    snapshot,
  ])
}
